// Data fetchers — pull the bulk payloads from the backend, annotate
// each terminal with freshness math, and trigger a full re-render.
//
// Migration note (Phase 6b, June 2026):
// The inline scripts in admin/index.php keep their own module-scope
// `let terminals`, `let groups`, `let users`, etc. that INITIALISE from
// window.timestop.state.* at parse time. After 6a, the bundle was REPLACING
// state.terminals / state.groups / etc. with fresh references on every
// fetch — but the inline lets still pointed at the OLD empty objects,
// so the inline renderers saw zero terminals / groups / users.
//
// The fix: mutate the existing state objects in place (clear + repopulate)
// instead of replacing the property. The inline lets then keep their
// reference and observe the populated data immediately. This mirrors the
// way auth.doLogin's recovery reload saw state.terminals go from 0 → 5
// keys (in 6a) and stopped needing the page reload as a band-aid.

import { apiFetch, type ApiFetchConfig } from './api';
import { driftSrv2Local, lastSeenFor } from './utils';
import { renderAll } from './render';
import type { TimestopState } from './state';
import type { CurrencyConfig } from './types';

// Replace every property on `target` with the entries from `next`.
// Object identity is preserved — callers that captured `target` keep
// seeing live updates. For arrays, we splice in place.
function replaceObjectInPlace(target: Record<string, unknown>, next: Record<string, unknown> | null | undefined): void {
  for (const k of Object.keys(target)) delete target[k];
  if (next && typeof next === 'object') {
    for (const [k, v] of Object.entries(next)) target[k] = v;
  }
}

function replaceArrayInPlace(target: unknown[], next: unknown): void {
  target.length = 0;
  if (Array.isArray(next)) {
    for (const item of next) target.push(item);
  }
}

// fetchData — pulls /api/data, annotates terminals with drift/onlineAgo/
// lastSeen, and triggers a full render. Returns nothing; side effects on
// `state` and the DOM. Mutates state.terminals / state.globalData /
// state.globalLogs / state.groups in place so the inline scripts' lets
// (which point at the same objects) see the fresh data.
export async function fetchData(
  state: TimestopState,
  config: ApiFetchConfig,
  showLoginModal: (msg?: string) => void,
): Promise<void> {
  if (!state.currentUser) {
    showLoginModal('Please sign in to continue.');
    return;
  }

  const r = await apiFetch('data', {}, config);
  if (!r.ok) return; // 401 already showed the modal

  const data = (r.data && typeof r.data === 'object' ? r.data : {}) as Record<string, unknown>;

  // Mutate the existing state objects in place — the inline scripts'
  // `let terminals = window.timestop.state.terminals` binding captured the
  // same reference at parse time, so live-mutating keeps it in sync.
  replaceObjectInPlace(
    state.globalData as unknown as Record<string, unknown>,
    data as Record<string, unknown>,
  );
  replaceObjectInPlace(
    state.terminals as unknown as Record<string, unknown>,
    (data.terminals && typeof data.terminals === 'object'
      ? (data.terminals as Record<string, unknown>)
      : null),
  );
  // globalLogs is no longer in the /api/data payload (Phase 12.11).
  // The Activity Logs page now calls fetchLogs() on demand when the
  // user navigates to it (see navigate(3) lazy-load in navigation.ts),
  // and the modal's Logs tab calls fetchLogs(mid) on first activation
  // (see renderTabLogs in terminal-modal.ts). The bulk dashboard fetch
  // stays log-free so a busy server doesn't pay for log rows the
  // caller isn't going to look at.
  replaceArrayInPlace(state.groups, data.groups);

  // Annotate each terminal with drift math.
  const terminals = state.terminals;
  const nowMs = new Date().toISOString().replace(/T|Z/g, ' ');
  const srvTime = (data.srvtime as string | undefined) || '';
  const driftSrv2LocalMs = driftSrv2Local(nowMs, srvTime);
  (state.globalData as { drift_srv2local?: number | null }).drift_srv2local = driftSrv2LocalMs;

  for (const t of Object.values(terminals)) {
    t.drift = driftSrv2Local(t.ts_serv, t.ts_spot) ?? undefined;
    const onlineAgo = driftSrv2Local(t.ts_serv, srvTime) ?? 0;
    (t as { onlineAgo?: number }).onlineAgo = onlineAgo;
    (t as { lastSeen?: Date | null }).lastSeen = lastSeenFor(t, driftSrv2LocalMs);
    t.status = onlineAgo < 45_000 ? 'Online' : 'Offline';
  }

  renderAll(state);
}

// fetchTerminal — pulls /api/terminal?id=N, returns a single terminal
// object (with logs). Does NOT mutate state; the caller decides how to
// merge the fresh data. Used by openTerminal (Feature 2) and the log-tab
// auto-refresh (Feature 3).
export async function fetchTerminal(
  id: number | string,
  config: ApiFetchConfig,
): Promise<Record<string, unknown> | null> {
  const r = await apiFetch(`terminal?id=${encodeURIComponent(String(id))}`, { method: 'GET' }, config);
  if (r.ok && r.data && typeof r.data === 'object') {
    return r.data as Record<string, unknown>;
  }
  return null;
}

export async function fetchGroups(
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<void> {
  let nextGroups: unknown[] = [];
  try {
    const r = await apiFetch('groups', { method: 'GET' }, config);
    if (r && r.ok && r.data) {
      nextGroups = Array.isArray(r.data) ? r.data : ((r.data as { groups?: unknown[] }).groups || []);
    }
  } catch {
    nextGroups = [];
  }
  replaceArrayInPlace(state.groups, nextGroups);
  // renderGroups lives in render.ts; we trigger it via window so the
  // inline-script wrapper (Phase 5+) and the bundle both work.
  const renderFn = (window as { renderGroups?: (s: TimestopState) => void }).renderGroups;
  if (renderFn) renderFn(state);
}

// fetchUsers — pulls /api/users, populates state.users in place,
// triggers a users-page render.
export async function fetchUsers(
  state: TimestopState,
  config: ApiFetchConfig,
  onUserAction: (uid: number | string, action: string) => void,
): Promise<void> {
  let nextUsers: unknown[] = [];
  try {
    const r = await apiFetch('users', { method: 'GET' }, config);
    if (r && r.ok && r.data) {
      nextUsers = Array.isArray(r.data) ? r.data : ((r.data as { users?: unknown[] }).users || []);
    }
  } catch {
    nextUsers = [];
  }
  replaceArrayInPlace(state.users, nextUsers);
  const renderFn = (window as { renderUsers?: (s: TimestopState, cb: typeof onUserAction) => void }).renderUsers;
  if (renderFn) renderFn(state, onUserAction);
}

// fetchLogs — pulls /api/logs[?mid=N] and updates state.
//
// Phase 12.11: logs are NOT in /api/data any more. The Activity Logs
// page (navigate(3)) calls fetchLogs() with no argument; the modal's
// Logs tab (renderTabLogs) calls fetchLogs(mid) on first activation
// and on every auto-refresh tick. Either way the response shape is
// the same: { logs: [{ts, mid, type, event, logval}, ...] }.
//
//   - Global (no mid): rows are stored on state.globalLogs and the
//     Activity Logs page repaints. The endpoint is role/group scoped
//     server-side (admin -> all terminals, operator -> their group
//     only), so an operator with no group sees an empty list.
//   - Per-terminal (mid): rows are stored on state.terminals[mid]._logs
//     and the modal's renderTerminalLogs() repaints.
//
// Returns the array of log rows (or [] on failure) so callers can
// decide whether to repaint; renderGlobalLogs / renderTerminalLogs
// are no-ops on empty input anyway.
export async function fetchLogs(
  state: TimestopState,
  config: ApiFetchConfig,
  opts: { mid?: number | string } = {},
): Promise<unknown[]> {
  const url = opts.mid != null && opts.mid !== ''
    ? `logs?mid=${encodeURIComponent(String(opts.mid))}`
    : 'logs';
  let logs: unknown[] = [];
  try {
    const r = await apiFetch(url, { method: 'GET' }, config);
    if (r && r.ok && r.data && typeof r.data === 'object') {
      const arr = (r.data as { logs?: unknown[] }).logs;
      logs = Array.isArray(arr) ? arr : [];
    }
  } catch {
    logs = [];
  }
  if (opts.mid != null && opts.mid !== '') {
    // Per-terminal: stash the rows on the terminal object so
    // renderTerminalLogs() can find them. We use a private `_logs`
    // key (underscore prefix) to avoid collisions with server-side
    // fields. Modal log polling also uses fetchLogs(mid) so this
    // single key covers both first-load and incremental refresh.
    const midKey = String(opts.mid);
    const t = state.terminals[midKey];
    if (t) {
      (t as { _logs?: unknown[] })._logs = logs;
    }
  } else {
    // Global: live-mutate state.globalLogs so any code that captured
    // the array reference (the same pattern as the rest of state.ts)
    // sees the fresh rows. If state.globalLogs is undefined (older
    // bootstrap that didn't init the field), assign a fresh array
    // first so replaceArrayInPlace has a target.
    if (!Array.isArray(state.globalLogs)) {
      state.globalLogs = [];
    }
    replaceArrayInPlace(state.globalLogs, logs);
  }
  return logs;
}

// fetchLoginAttempts — pulls /api/login_attempts (admin-only endpoint),
// stores the payload on state.loginAttempts, then triggers
// renderLoginAttempts so the "Recent login attempts" section + the
// rate-limit badges on each userCard repaint.
//
// Designed to be called:
//   - right after fetchUsers() when the admin opens the Users page
//   - on a slow auto-refresh timer while that page is visible
//   - on demand after a password change / user delete
//
// Failures are silent: if the endpoint is not yet available (older
// PHP deploy) we just keep the previous state and bail.
export async function fetchLoginAttempts(
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<void> {
  try {
    const r = await apiFetch('login_attempts?limit=50', { method: 'GET' }, config);
    if (!r || !r.ok || !r.data || typeof r.data !== 'object') return;
    const payload = r.data as Record<string, unknown>;
    // Live-mutate the existing object identity so any code that
    // captured state.loginAttempts (the same pattern as
    // replaceObjectInPlace used elsewhere) sees the fresh values.
    for (const k of Object.keys(state.loginAttempts)) delete (state.loginAttempts as Record<string, unknown>)[k];
    for (const [k, v] of Object.entries(payload)) (state.loginAttempts as Record<string, unknown>)[k] = v;
  } catch {
    // Keep previous state on transient network failure.
    return;
  }
  const renderFn = (window as { renderLoginAttempts?: (s: TimestopState) => void }).renderLoginAttempts;
  if (renderFn) renderFn(state);
}

// loadCurrencyDefaults — pull /api/currencies and populate
// state.currencyDefaults with the per-ISO config map.
//
// Called once at login (after state.currentUser is set, before
// fetchData so the Dashboard's first revenue render already has the
// BO-wide defaults) and again on the page-reload path (existing
// session, inline `window.addEventListener('load', ...)` in
// admin/index.php). Idempotent: re-runs wipe the existing map and
// re-populate from the latest server response.
//
// Failure modes (all silent — never block the BO):
//   - HTTP error / 401 / network blip: state.currencyDefaults stays
//     as-is (empty if this is the first run). The hard-coded
//     {div:100, fs:'%.2f '+iso} fallback in buildFallbackCurrency
//     takes over.
//   - Table doesn't exist (host hasn't run the migration):
//     /api/currencies returns `{ok:true, currencies:{}}` and we
//     mirror that — empty map, hard-coded fallback does the work.
//   - Response shape unexpected: log + leave state untouched.
//
// We mutate state.currencyDefaults in place (clear + repopulate) so
// any code that captured the object sees the fresh rows immediately.
export async function loadCurrencyDefaults(
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<void> {
  try {
    const r = await apiFetch('currencies', { method: 'GET' }, config);
    if (!r || !r.ok || !r.data || typeof r.data !== 'object') return;
    const payload = r.data as { currencies?: Record<string, unknown> };
    if (!payload.currencies || typeof payload.currencies !== 'object') return;
    // Wipe + repopulate. Live mutation, same pattern as
    // replaceObjectInPlace used in fetchData() above.
    for (const k of Object.keys(state.currencyDefaults)) {
      delete state.currencyDefaults[k];
    }
    for (const [iso, raw] of Object.entries(payload.currencies)) {
      if (!raw || typeof raw !== 'object') continue;
      const cfg = raw as Partial<CurrencyConfig>;
      if (typeof cfg.ISO !== 'string') continue;
      const upper = cfg.ISO.toUpperCase();
      state.currencyDefaults[upper] = {
        ISO: upper,
        div: typeof cfg.div === 'number' ? cfg.div : 100,
        fs: typeof cfg.fs === 'string' ? cfg.fs : '%.2f ' + upper,
        // step drives the price-input nudge in the Settings tab
        // (1 for CZK, 100 for JPY/KRW/VND/BHD/KWD/OMR/TND, 10
        // everywhere else). Falls back to 10 when the field is
        // missing — e.g. hosts whose /api/currencies predates the
        // step column or whose PHP skipped the SELECT.
        step: typeof cfg.step === 'number' ? cfg.step : 10,
      };
    }
  } catch {
    // Transient failure — keep whatever was there. First-ever run
    // stays with {} from createInitialState and the hard-coded
    // fallback in buildFallbackCurrency handles formatting.
    return;
  }
}

// fetchNotify — pulls /api/notify (the caller's pending notify feed,
// max 100 rows) and replaces state.notify in place. Returns the
// resolved array so callers (the 30s dashboard ticker) can decide
// whether to repaint. The dashboard's renderNotify() is also called
// on resolve, so the panel lights up the moment the fetch lands.
//
// Shape (from /api/notify, see admin/api/index.php): the same
// {ts, mid, event, logval, type} LogEntry shape /api/logs ships, so
// renderNotify() can reuse the existing terminalGlobalLogRow() row
// helper without any client-side translation. The row's `id` field
// is the notify-table PK, used by the click handler to call
// /api/notifyRead; _mach_name / _mach_loc are the server-joined
// terminal name/location, used as a fallback when the SPA hasn't
// loaded the terminal into state.terminals yet.
//
// The endpoint is auth-gated and server-side-scoped to the caller's
// aid, so an operator never sees anyone else's feed. We don't try to
// dedupe on the client; the server's `ORDER BY id DESC LIMIT N` is
// authoritative.
export async function fetchNotify(
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<unknown[]> {
  if (!state.currentUser) return [];
  if (!Array.isArray(state.notify)) state.notify = [];
  let rows: unknown[] = [];
  try {
    const r = await apiFetch('notify?limit=50', { method: 'GET' }, config);
    if (r && r.ok && r.data && typeof r.data === 'object') {
      // Server ships `{ok, logs: [...]}`, matching the /api/logs
      // envelope so the renderer can use the same LogEntry-typed
      // helpers.
      const arr = (r.data as { logs?: unknown[] }).logs;
      rows = Array.isArray(arr) ? arr : [];
    }
  } catch {
    rows = [];
  }
  replaceArrayInPlace(state.notify, rows);
  const renderFn = (window as { renderNotify?: (s: TimestopState) => void }).renderNotify;
  if (renderFn) renderFn(state);
  return rows;
}

// markNotifyRead — POST /api/notifyRead with the row's id, then
// remove the row from state.notify in place and repaint the panel.
//
// Optimistic-ish: we strip the row from the array BEFORE the network
// call so the click feels instant; the server is the source of truth
// for the move (notify -> notify_arch) and on a 5xx we put the row
// back at the head of the array and re-render. A 4xx (bad id) just
// drops the row — the server's "already archived" path is benign.
//
// Returns the api response so callers can chain (or ignore it).
export async function markNotifyRead(
  state: TimestopState,
  config: ApiFetchConfig,
  id: number | string,
): Promise<unknown> {
  if (!state.currentUser) return null;
  if (!Array.isArray(state.notify)) state.notify = [];
  const idNum = Number(id);
  if (!Number.isFinite(idNum) || idNum <= 0) return null;

  // Snapshot the row + position so we can roll back on a 5xx.
  const idx = state.notify.findIndex((r) => {
    const rid = (r as { id?: unknown }).id;
    return Number(rid) === idNum;
  });
  const snapshot = idx >= 0 ? state.notify[idx] : null;

  // Optimistic remove. Uses the incremental DOM path
  // (removeNotifyRow) so the other buckets — and any bucket the
  // user has expanded — keep their open/closed state and scroll
  // position. The full re-render path (renderNotify) wipes
  // innerHTML and would collapse every <details>.
  if (idx >= 0) {
    state.notify.splice(idx, 1);
    const removeFn = (window as { removeNotifyRow?: (s: TimestopState, id: number) => void }).removeNotifyRow;
    if (removeFn) removeFn(state, idNum);
  }

  let r;
  try {
    r = await apiFetch('notifyRead', { method: 'POST', body: { id: idNum } }, config);
  } catch (e) {
    // Network blip — put the row back at its original position and repaint.
    if (snapshot) {
      const pos = Math.min(idx >= 0 ? idx : state.notify.length, state.notify.length);
      state.notify.splice(pos, 0, snapshot);
      const renderFn = (window as { renderNotify?: (s: TimestopState) => void }).renderNotify;
      if (renderFn) renderFn(state);
    }
    return { ok: false, status: 0, data: { error: 'network', detail: String(e) } };
  }

  // 4xx (e.g. missing_id) — keep the optimistic removal, the row is
  // already gone from the UI; a 5xx is server-side trouble so we
  // restore. status === 0 is a network failure handled above.
  if (r && typeof r.status === 'number' && r.status >= 500 && snapshot) {
    const pos = Math.min(idx >= 0 ? idx : state.notify.length, state.notify.length);
    state.notify.splice(pos, 0, snapshot);
    const renderFn = (window as { renderNotify?: (s: TimestopState) => void }).renderNotify;
    if (renderFn) renderFn(state);
  }
  return r;
}
